import React from 'react';
import type { WorkflowNode, WorkflowEdge } from '../../../../types/workflow';
import type { ActionPageContext } from '../types';

type UseNodeGraphMapsParams = {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  selectedNodeForConfig: WorkflowNode | null;
  credentialPropertyIdMap: Map<string, string | null>;
};

export function useNodeGraphMaps({
  nodes,
  edges,
  selectedNodeForConfig,
  credentialPropertyIdMap,
}: UseNodeGraphMapsParams) {
  const nodeById = React.useMemo(() => {
    return new Map(nodes.map((node) => [node.id, node]));
  }, [nodes]);

  const incomingEdgesByTarget = React.useMemo(() => {
    const map = new Map<string, string[]>();
    edges.forEach((edge) => {
      const existing = map.get(edge.target);
      if (existing) {
        existing.push(edge.source);
      } else {
        map.set(edge.target, [edge.source]);
      }
    });
    return map;
  }, [edges]);

  const inferPageContextForNode = React.useCallback(
    (nodeId: string): ActionPageContext | null => {
      const visited = new Set<string>();
      const queue = [...(incomingEdgesByTarget.get(nodeId) ?? [])];

      while (queue.length > 0) {
        const currentId = queue.shift();
        if (!currentId || visited.has(currentId)) {
          continue;
        }
        visited.add(currentId);

        const candidate = nodeById.get(currentId);
        if (!candidate) {
          continue;
        }

        if (candidate.type === 'navigate') {
          const data = (candidate.data ?? {}) as Record<string, any>;
          return {
            canonicalPageId: (data.canonicalPageId as string | null | undefined) ?? null,
            pageTitle: (data.pageTitle as string | null | undefined) ?? null,
            pageUrl: (data.pageUrl as string | null | undefined) ?? null,
          };
        }

        const upstream = incomingEdgesByTarget.get(currentId);
        if (upstream && upstream.length > 0) {
          queue.push(...upstream);
        }
      }

      return null;
    },
    [incomingEdgesByTarget, nodeById]
  );

  const inferPropertyIdForNode = React.useCallback(
    (nodeId: string): string | null => {
      const visited = new Set<string>();
      const queue = [...(incomingEdgesByTarget.get(nodeId) ?? [])];

      while (queue.length > 0) {
        const currentId = queue.shift();
        if (!currentId || visited.has(currentId)) {
          continue;
        }
        visited.add(currentId);

        const candidate = nodeById.get(currentId);
        if (!candidate) {
          continue;
        }

        if (candidate.type === 'login') {
          const credentialId = (candidate.data as Record<string, unknown>)?.credentialId;
          if (typeof credentialId === 'string') {
            return credentialPropertyIdMap.get(credentialId) ?? null;
          }
          return null;
        }

        const upstream = incomingEdgesByTarget.get(currentId);
        if (upstream && upstream.length > 0) {
          queue.push(...upstream);
        }
      }

      return null;
    },
    [incomingEdgesByTarget, nodeById, credentialPropertyIdMap]
  );

  const selectedNodePageContext = React.useMemo<ActionPageContext | null>(() => {
    if (!selectedNodeForConfig) {
      return null;
    }
    return inferPageContextForNode(selectedNodeForConfig.id);
  }, [inferPageContextForNode, selectedNodeForConfig]);

  const selectedNodePropertyId = React.useMemo<string | null>(() => {
    if (!selectedNodeForConfig) {
      return null;
    }
    return inferPropertyIdForNode(selectedNodeForConfig.id);
  }, [inferPropertyIdForNode, selectedNodeForConfig]);

  const inferUpstreamSeedForNode = React.useCallback(
    (nodeId: string): string | null => {
      const visited = new Set<string>();
      const queue = [...(incomingEdgesByTarget.get(nodeId) ?? [])];

      while (queue.length > 0) {
        const currentId = queue.shift();
        if (!currentId || visited.has(currentId)) {
          continue;
        }
        visited.add(currentId);

        const candidate = nodeById.get(currentId);
        if (!candidate) {
          continue;
        }

        if (candidate.type === 'seed') {
          const seedWorkflowId = (candidate.data as Record<string, unknown>)?.seedWorkflowId;
          if (typeof seedWorkflowId === 'string' && seedWorkflowId) {
            return seedWorkflowId;
          }
          return null;
        }

        const upstream = incomingEdgesByTarget.get(currentId);
        if (upstream && upstream.length > 0) {
          queue.push(...upstream);
        }
      }

      return null;
    },
    [incomingEdgesByTarget, nodeById]
  );

  const selectedNodeUpstreamSeedWorkflowId = React.useMemo<string | null>(() => {
    if (!selectedNodeForConfig) {
      return null;
    }
    return inferUpstreamSeedForNode(selectedNodeForConfig.id);
  }, [inferUpstreamSeedForNode, selectedNodeForConfig]);

  return {
    nodeById,
    incomingEdgesByTarget,
    inferPageContextForNode,
    inferPropertyIdForNode,
    inferUpstreamSeedForNode,
    selectedNodePageContext,
    selectedNodePropertyId,
    selectedNodeUpstreamSeedWorkflowId,
  };
}
